import { Fragment, useMemo, useState } from 'react'
import {
  useEventRisks,
  useSignOffEventRisk,
  useDeleteEventRisk
} from '../../hooks/useRisks'
import { useArchived } from '../../contexts/ArchiveContext'
import { useConfirm } from '../shared/ConfirmProvider'
import {
  bandFor,
  residualScore,
  inherentScore,
  type RiskBand
} from '../../lib/risk'
import { RiskEditorV2 } from './RiskEditorV2'
import type { EventListItem, EventRisk } from '../../types'

// RiskRegisterTabV2 — spec 12 v2 surface (UX framed by Chris as the
// "Risk Register"). Gated by ?v2=risks. Lifts v1 EventRisks data into
// the v2 layout: filter pills + cards on the left, residual heatmap +
// sign-off card on the right.
//
// All scoring math goes through src/lib/risk.ts (bandFor, inherent/
// residualScore) so the band thresholds stay coherent with the test
// matrix.

type SeverityFilter = 'all' | 'high'
type StatusFilter = 'all' | 'open' | 'signed'

interface Props {
  event: EventListItem
}

export function RiskRegisterTabV2({ event }: Props) {
  const archived = useArchived()
  const { data: risks = [], isLoading, error } = useEventRisks(event.id)
  const [editing, setEditing] = useState<EventRisk | 'new' | null>(null)
  const [severity, setSeverity] = useState<SeverityFilter>('all')
  const [status, setStatus] = useState<StatusFilter>('all')

  const counts = useMemo(() => {
    const all = risks.length
    const high = risks.filter((r) => residualScore(toScorable(r)) >= 12).length
    const open = risks.filter((r) => !r.signedOff?.at).length
    const signed = risks.filter((r) => !!r.signedOff?.at).length
    return { all, high, open, signed }
  }, [risks])

  const filtered = useMemo(() => {
    return risks.filter((r) => {
      if (severity === 'high' && residualScore(toScorable(r)) < 12) return false
      if (status === 'open' && r.signedOff?.at) return false
      if (status === 'signed' && !r.signedOff?.at) return false
      return true
    })
  }, [risks, severity, status])

  if (isLoading) {
    return <div className="text-ink-soft text-sm font-mono">Loading risks…</div>
  }
  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load risks</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex items-baseline gap-3">
        <h2 className="font-display text-xl">Risk Register</h2>
        <span
          className="text-xs uppercase tracking-wide font-semibold text-green border border-green/50 px-2 py-0.5 rounded"
          title="Risks v2 — enabled via ?v2=risks"
        >
          v2
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        <div>
          <FilterBar
            counts={counts}
            severity={severity}
            status={status}
            archived={archived}
            onSeverity={setSeverity}
            onStatus={setStatus}
            onAdd={() => setEditing('new')}
          />
          <div className="flex flex-col gap-3">
            {filtered.length === 0 ? (
              <EmptyRisks onAdd={() => setEditing('new')} hasAny={risks.length > 0} archived={archived} />
            ) : (
              filtered.map((r) => (
                <RiskCard key={r.id} risk={r} archived={archived} onEdit={() => setEditing(r)} />
              ))
            )}
          </div>
        </div>
        <aside className="flex flex-col gap-4">
          <RiskHeatmap risks={risks} />
          <SignoffCard risks={risks} />
        </aside>
      </div>

      {!archived && editing && (
        <RiskEditorV2
          event={event}
          risk={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  )
}

// ---- Filter bar -----------------------------------------------------------

function FilterBar({
  counts,
  severity,
  status,
  archived,
  onSeverity,
  onStatus,
  onAdd
}: {
  counts: { all: number; high: number; open: number; signed: number }
  severity: SeverityFilter
  status: StatusFilter
  archived: boolean
  onSeverity: (s: SeverityFilter) => void
  onStatus: (s: StatusFilter) => void
  onAdd: () => void
}) {
  return (
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <div className="flex flex-wrap gap-1.5">
        <Pill
          active={severity === 'all' && status === 'all'}
          onClick={() => {
            onSeverity('all')
            onStatus('all')
          }}
        >
          All ({counts.all})
        </Pill>
        <Pill active={severity === 'high'} onClick={() => onSeverity(severity === 'high' ? 'all' : 'high')}>
          High &amp; Severe ({counts.high})
        </Pill>
        <Pill active={status === 'open'} onClick={() => onStatus(status === 'open' ? 'all' : 'open')}>
          Open ({counts.open})
        </Pill>
        <Pill active={status === 'signed'} onClick={() => onStatus(status === 'signed' ? 'all' : 'signed')}>
          Signed off ({counts.signed})
        </Pill>
      </div>
      {!archived && (
        <button
          type="button"
          onClick={onAdd}
          className="text-xs font-semibold text-parchment bg-green hover:bg-green/90 rounded px-3 py-1.5"
        >
          + Add risk
        </button>
      )}
    </div>
  )
}

function Pill({
  active,
  onClick,
  children
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'px-2.5 py-1 text-xs font-semibold rounded border border-green bg-green/10 text-green'
          : 'px-2.5 py-1 text-xs rounded border border-line text-ink-soft hover:bg-parchment'
      }
    >
      {children}
    </button>
  )
}

// ---- Risk card ------------------------------------------------------------

function RiskCard({ risk, archived, onEdit }: { risk: EventRisk; archived: boolean; onEdit: () => void }) {
  const confirm = useConfirm()
  const remove = useDeleteEventRisk(risk.eventId)
  const signOff = useSignOffEventRisk(risk.eventId)
  const scorable = toScorable(risk)
  const inhBand = bandFor(inherentScore(scorable))
  const resBand = bandFor(residualScore(scorable))
  const signed = !!risk.signedOff?.at

  async function handleDelete(e: React.MouseEvent) {
    e.stopPropagation()
    const ok = await confirm({
      title: 'Delete this risk?',
      body: `"${risk.hazard}" will be removed from the register.`,
      tone: 'destructive',
      confirmLabel: 'Delete'
    })
    if (ok) remove.mutate(risk.id)
  }

  function handleSignOff(e: React.MouseEvent) {
    e.stopPropagation()
    signOff.mutate(risk.id)
  }

  return (
    <div
      onClick={archived ? undefined : onEdit}
      className={
        'bg-white border border-line rounded-lg p-4 transition-colors ' +
        (archived ? '' : 'hover:border-green cursor-pointer')
      }
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-ink-soft mb-1">
            {risk.category}
            {risk.dept ? <> · {risk.dept}</> : null}
          </div>
          <div className="font-display text-base font-semibold leading-tight">
            {risk.hazard}
          </div>
          {risk.description && (
            <div className="text-xs text-ink-soft mt-1 line-clamp-2">
              {risk.description}
            </div>
          )}
        </div>
        {signed ? (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-green bg-green/10 border border-green/40 rounded px-2 py-0.5 whitespace-nowrap">
            ✓ Signed off
          </span>
        ) : (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-oxblood bg-oxblood-soft border border-oxblood/40 rounded px-2 py-0.5 whitespace-nowrap">
            Open
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-3">
        <ScoreLine
          label="Inherent"
          l={scorable.inherentL}
          s={scorable.inherentS}
          band={inhBand}
        />
        <ScoreLine
          label="Residual"
          l={scorable.residualL}
          s={scorable.residualS}
          band={resBand}
        />
      </div>

      {risk.controls && (
        <div className="text-xs text-ink-soft leading-relaxed">
          <span className="font-semibold uppercase tracking-wide text-ink">Controls · </span>
          <span className="line-clamp-2">{risk.controls}</span>
        </div>
      )}

      <div className="flex items-center justify-between mt-3 pt-2 border-t border-line">
        <div className="text-xs text-ink-soft">
          {risk.owner ? `Owner: ${risk.owner.name ?? '—'}` : 'No owner'}
          {signed && risk.signedOff?.at && (
            <> · Signed {new Date(risk.signedOff.at).toLocaleDateString('en-GB')}</>
          )}
        </div>
        {!archived && (
          <div className="flex items-center gap-1.5">
            {!signed && (
              <button
                type="button"
                onClick={handleSignOff}
                disabled={signOff.isPending}
                className="text-xs text-green hover:text-green/80 disabled:opacity-50"
              >
                Sign off
              </button>
            )}
            <button
              type="button"
              onClick={handleDelete}
              disabled={remove.isPending}
              aria-label="Delete risk"
              className="text-xs text-ink-soft hover:text-oxblood disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

function ScoreLine({
  label,
  l,
  s,
  band
}: {
  label: string
  l: 1 | 2 | 3 | 4 | 5
  s: 1 | 2 | 3 | 4 | 5
  band: RiskBand
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-ink-soft mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-semibold">
          L{l} × S{s} = {l * s}
        </span>
        <BandChip band={band} />
      </div>
    </div>
  )
}

const BAND_STYLES: Record<RiskBand, string> = {
  low: 'bg-green/10 text-green border-green/40',
  med: 'bg-brass/10 text-brass border-brass/50',
  high: 'bg-oxblood-soft text-oxblood border-oxblood/40',
  severe: 'bg-oxblood text-parchment border-oxblood'
}

function BandChip({ band }: { band: RiskBand }) {
  return (
    <span
      className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${BAND_STYLES[band]}`}
    >
      {band}
    </span>
  )
}

// ---- Empty ----------------------------------------------------------------

function EmptyRisks({
  onAdd,
  hasAny,
  archived
}: {
  onAdd: () => void
  hasAny: boolean
  archived: boolean
}) {
  return (
    <div className="bg-white border border-line border-dashed rounded-lg p-8 text-center">
      <div className="font-semibold mb-1">
        {hasAny ? 'No risks match the current filter' : 'No risks logged yet'}
      </div>
      <div className="text-sm text-ink-soft mb-3">
        {hasAny
          ? 'Adjust the filter pills above, or add a new risk.'
          : 'Add risks from the hazard library or capture event-specific ones.'}
      </div>
      {!archived && (
        <button
          type="button"
          onClick={onAdd}
          className="text-xs font-semibold text-parchment bg-green hover:bg-green/90 rounded px-3 py-1.5"
        >
          + Add risk
        </button>
      )}
    </div>
  )
}

// ---- Heatmap --------------------------------------------------------------

function RiskHeatmap({ risks }: { risks: EventRisk[] }) {
  // Bucket by (residualL, residualS)
  const cells = useMemo(() => {
    const m = new Map<string, EventRisk[]>()
    for (const r of risks) {
      const key = `${r.residualLikelihood}-${r.residualSeverity}`
      const arr = m.get(key)
      if (arr) arr.push(r)
      else m.set(key, [r])
    }
    return m
  }, [risks])

  const cellBg: Record<RiskBand, string> = {
    low: 'bg-green/10 text-green',
    med: 'bg-brass/15 text-brass',
    high: 'bg-oxblood-soft text-oxblood',
    severe: 'bg-oxblood text-parchment'
  }

  return (
    <div className="bg-white border border-line rounded-lg p-4">
      <div className="text-sm font-semibold mb-3">Residual heatmap</div>
      <div className="grid gap-1" style={{ gridTemplateColumns: '14px repeat(5, 1fr) ', aspectRatio: '6 / 5' }}>
        {[5, 4, 3, 2, 1].map((l) => (
          <Fragment key={l}>
            <div className="flex items-center justify-center text-[9px] text-ink-soft font-bold">{l}</div>
            {[1, 2, 3, 4, 5].map((s) => {
              const score = l * s
              const band = bandFor(score)
              const bucket = cells.get(`${l}-${s}`) ?? []
              return (
                <div
                  key={s}
                  className={`relative rounded text-[10px] font-bold flex items-center justify-center ${cellBg[band]}`}
                  title={bucket.map((r) => r.hazard).join(', ') || `Score ${score}`}
                >
                  {score}
                  {bucket.length > 0 && (
                    <span className="absolute top-0.5 right-0.5 bg-ink text-parchment text-[8px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                      {bucket.length}
                    </span>
                  )}
                </div>
              )
            })}
          </Fragment>
        ))}
        <div />
        {[1, 2, 3, 4, 5].map((s) => (
          <div key={s} className="text-[9px] text-ink-soft font-bold text-center">
            {s}
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-2 text-[10px] text-ink-soft">
        <span>← Likelihood</span>
        <span>Severity →</span>
      </div>
    </div>
  )
}

// ---- Sign-off card --------------------------------------------------------

function SignoffCard({ risks }: { risks: EventRisk[] }) {
  const total = risks.length
  const signed = risks.filter((r) => !!r.signedOff?.at).length
  const pct = total === 0 ? 0 : Math.round((signed / total) * 100)
  // Final RA approval lives on the event row (event.raSignedOffAt) but
  // the EventListItem type doesn't carry it yet — placeholder UI until
  // the event handler exposes the new columns.
  const finalApproved = false

  return (
    <div className="bg-white border border-line rounded-lg p-4">
      <div className="text-sm font-semibold mb-3">Sign-off</div>
      <div className="text-xs text-ink-soft mb-2">
        {signed} of {total} risks signed off
      </div>
      <div className="h-1.5 bg-parchment rounded-full overflow-hidden mb-4">
        <div
          className={`h-full transition-all ${pct === 100 ? 'bg-green' : 'bg-brass'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="text-[10px] uppercase tracking-wide font-semibold text-ink-soft mb-1">
        Final RA approval
      </div>
      {finalApproved ? (
        <div className="text-xs text-green">✓ Approved</div>
      ) : signed < total ? (
        <div className="text-xs text-ink-soft">
          Awaiting {total - signed} more risk{total - signed === 1 ? '' : 's'}
        </div>
      ) : (
        <div className="text-xs text-ink-soft italic">
          All risks signed; final approval surface pending events handler update.
        </div>
      )}
    </div>
  )
}

// ---- Shape adapter --------------------------------------------------------

// v1 wire shape uses full likelihood/severity field names; the v2 risk
// lib expects the typed L/S shape. Coerce to 1..5 range — backstops a
// data bug rather than crashing the renderer.
function toScorable(r: EventRisk) {
  const clamp = (n: number) => Math.min(5, Math.max(1, Math.round(n)))
  return {
    inherentL: clamp(r.inherentLikelihood) as 1 | 2 | 3 | 4 | 5,
    inherentS: clamp(r.inherentSeverity) as 1 | 2 | 3 | 4 | 5,
    residualL: clamp(r.residualLikelihood) as 1 | 2 | 3 | 4 | 5,
    residualS: clamp(r.residualSeverity) as 1 | 2 | 3 | 4 | 5
  }
}
